import { z } from 'zod';

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return (
      !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
    );
  }, 'Invalid ISO date');

const oneOrMany = <T extends z.ZodTypeAny>(schema: T) =>
  z
    .union([schema, z.array(schema)])
    .transform((value) => (Array.isArray(value) ? value : [value]));

// The Cancelled sales stage. Duplicated from `SALES_STAGE.CANCELLED` in
// `apps/itineraries/api/src/itineraries/status-fsm.ts` / `CANCELLED_STAGE` in
// `@travel-planner/api-shared/itinerary-sale-dates` because both of those are
// backend-only modules and the cancelled-itineraries report service needs the
// value. Pinned by a test in the api-shared package; if these ever disagree,
// that constant is the authority.
export const CANCELLED_SALES_STAGE = 20;

export const itinerarySearchSortBy = z.enum([
  'travelDate',
  'reference',
  'title',
  'modifiedOn',
  'createdOn',
  'price',
  'duration',
  'viewedTimes',
  'customer',
  'travelers',
  'status',
]);

export type ItinerarySearchSortBy = z.infer<typeof itinerarySearchSortBy>;

export const itinerarySearchSortOrder = z.enum(['asc', 'desc']);

export type ItinerarySearchSortOrder = z.infer<typeof itinerarySearchSortOrder>;

export const itinerarySearchInput = z
  .object({
    title: z.string().optional(),
    reference: z.string().optional(),
    optigest: z.string().optional(),
    customerName: z.string().optional(),
    status: oneOrMany(z.uuid()).optional(),
    duration: oneOrMany(z.number().int().nonnegative()).optional(),
    // Duration range (agent-write-ontology #331). Inclusive bounds on the
    // active version's `durationDays`; independent of the exact `duration`
    // list above (a caller may pass either). Backs the agent's `duration > 5`.
    minDuration: z.number().int().nonnegative().optional(),
    maxDuration: z.number().int().nonnegative().optional(),
    // Price range (#331) — inclusive bounds on the active version's `price`,
    // base currency EUR. Backs the `high value` rule (`price > 5000eur`) and
    // the agent's price comparisons, which the list previously could not run.
    minPrice: z.number().nonnegative().optional(),
    maxPrice: z.number().nonnegative().optional(),
    owners: oneOrMany(z.uuid()).optional(),
    customers: oneOrMany(z.uuid()).optional(),
    label: oneOrMany(z.string().min(1)).optional(),
    startDate: dateOnly.optional(),
    endDate: dateOnly.optional(),
    excludeStartDate: dateOnly.optional(),
    excludeEndDate: dateOnly.optional(),
    // Inclusive range on `itinerary.created_on` (the date the itinerary was
    // created) — distinct from the travel-date range above. Both bounds must
    // be supplied together (enforced by the superRefine below).
    createdStartDate: dateOnly.optional(),
    createdEndDate: dateOnly.optional(),
    travelDate: dateOnly.optional(),
    // Single ISO country code or full name. Case-insensitive equality
    // against `itinerary.country` (text enum). Backs the cms-public
    // mood-picker country filter on the itineraries section.
    country: z.string().optional(),
    // Hashtag overlap (jsonb tags ANY-of, case-insensitive). Reuses the
    // same lowercase + remove_diacritics_with_spaces normalisation as
    // the `label` filter; the two are kept separate so URL callers can
    // pass label-style strings unchanged while the cms-public discovery
    // store passes its mood-picker hashtags.
    hashtags: oneOrMany(z.string().min(1)).optional(),
    // Publish gate for the public storefront. Opt-in and absent by default:
    // the console's own callers (CRM contact table, task-manager pickers)
    // must keep seeing unpublished itineraries, so only cms-public passes it.
    // `true` matches the storefront's CMS-DB read — NULL counts as hidden.
    isShownOnCms: z.boolean().optional(),
    myItineraries: z.boolean().optional(),
    // XT-723 — narrow the list to itineraries carrying unanswered Customer
    // Preview comments (see `pendingComments` on `itinerarySearchItem` for the
    // rule). Boolean toggle, same shape as `myItineraries`; absent = no filter.
    pendingComments: z.boolean().optional(),
    viewed: z.number().int().nonnegative().optional(),
    sortBy: itinerarySearchSortBy.optional(),
    sortOrder: itinerarySearchSortOrder.optional(),
    offset: z.number().int().nonnegative().default(0),
    limit: z.number().int().positive().max(100).default(20),
  })
  .strict()
  .superRefine((input, ctx) => {
    if (
      (input.startDate && !input.endDate) ||
      (!input.startDate && input.endDate)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'startDate and endDate must be provided together',
        path: input.startDate ? ['endDate'] : ['startDate'],
      });
    }
    if (
      (input.excludeStartDate && !input.excludeEndDate) ||
      (!input.excludeStartDate && input.excludeEndDate)
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'excludeStartDate and excludeEndDate must be provided together',
        path: input.excludeStartDate
          ? ['excludeEndDate']
          : ['excludeStartDate'],
      });
    }
    if (
      (input.createdStartDate && !input.createdEndDate) ||
      (!input.createdStartDate && input.createdEndDate)
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'createdStartDate and createdEndDate must be provided together',
        path: input.createdStartDate
          ? ['createdEndDate']
          : ['createdStartDate'],
      });
    }
  });

export type ItinerarySearchInput = z.infer<typeof itinerarySearchInput>;

// URL-side filter shape consumed by the /itineraries page (TanStack Router's
// `validateSearch`). Subset of `itinerarySearchInput` minus the
// transport-layer `offset` / `limit` (FE uses `page` + pageSize) and with
// `labels` (plural, URL-friendly) renamed from the backend's `label`. All
// fields optional; `undefined` keys stay undefined so the tRPC transformer
// strips them. The page → offset and labels → label translation lives in
// `features/itineraries-filters/model/to-search-input.ts`.
//
// Authoritative purpose: the `ItinerariesListSearch` type below is the
// source of truth for the URL-state shape (consumed by the agent-panel
// `apply_filters` schema + adapter in Phase 5). The hand-rolled coercer
// `validateItinerariesSearch` is intentionally NOT a `.parse` call on this
// schema — TanStack Router's `validateSearch` contract forbids throwing,
// and the coercer must accept partially-invalid URLs without rejecting the
// whole search. Keep both shapes structurally aligned by hand.
export const itinerariesListSearch = z.object({
  title: z.string().optional(),
  reference: z.string().optional(),
  optigest: z.string().optional(),
  customerName: z.string().optional(),
  status: z.array(z.uuid()).optional(),
  duration: z.array(z.number().int().nonnegative()).optional(),
  // Agent-applied range filters (#331) — kept aligned with `itinerarySearchInput`.
  minDuration: z.number().int().nonnegative().optional(),
  maxDuration: z.number().int().nonnegative().optional(),
  minPrice: z.number().nonnegative().optional(),
  maxPrice: z.number().nonnegative().optional(),
  owners: z.array(z.uuid()).optional(),
  customers: z.array(z.uuid()).optional(),
  labels: z.array(z.string().min(1)).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  createdStartDate: z.string().optional(),
  createdEndDate: z.string().optional(),
  travelDate: z.string().optional(),
  country: z.string().optional(),
  hashtags: z.array(z.string().min(1)).optional(),
  myItineraries: z.boolean().optional(),
  pendingComments: z.boolean().optional(),
  viewed: z.number().int().nonnegative().optional(),
  sortBy: itinerarySearchSortBy.optional(),
  sortOrder: itinerarySearchSortOrder.optional(),
  page: z.number().int().positive().optional(),
});

export type ItinerariesListSearch = z.infer<typeof itinerariesListSearch>;

export const itinerarySearchStatus = z.object({
  id: z.string(),
  status: z.string().nullable(),
  // Consultant-facing hex (e.g. '#3b82f6'). Joined from
  // `itinerary_status.color`; used to render the badge on each row so
  // every status reads distinctly. Falls back to a neutral if null.
  color: z.string().nullable(),
});

export type ItinerarySearchStatus = z.infer<typeof itinerarySearchStatus>;

// Richer status shape returned by `itineraries.statuses` (the filter
// catalog). `salesStage` is the FSM numeric — EXCLUDED=0, DRAFT=1,
// PROPOSAL=4, SOLD=10, CANCELLED=20 (see `SALES_STAGE` in
// `apps/itineraries/api/src/itineraries/status-fsm.ts`; real org catalogs
// also use 7 and 9, which `parseSalesStage` maps to null, so anything
// reasoning about stage bands must read the raw number).
// `color` is the consultant-facing hex.
// Used by the Status MultiSelect on the /itineraries page.
/**
 * Traveller Preview access an organization sets per itinerary status: closed,
 * open without prices, or open with prices. A status with no value (null)
 * keeps the built-in rules.
 */
export const travellerPreviewAccessValues = [
  'not_available',
  'without_prices',
  'with_prices',
] as const;
export const travellerPreviewAccessSchema = z.enum(
  travellerPreviewAccessValues
);
export type TravellerPreviewAccess = z.infer<
  typeof travellerPreviewAccessSchema
>;

/**
 * The step an itinerary status plays in the approval process, set per
 * organization. Null keeps the name-based detection.
 */
export const itineraryProcessStepValues = [
  'created',
  'price_review',
  'ready_for_review',
  'in_review',
  'quotation_sent',
  'confirmed',
] as const;
export const itineraryProcessStepSchema = z.enum(itineraryProcessStepValues);
export type ItineraryProcessStep = z.infer<typeof itineraryProcessStepSchema>;
export const itineraryProcessTargetSchema = z.enum([
  ...itineraryProcessStepValues,
  'review_completed',
  'reopened',
  'sale_revoked',
  'shared_with_agent',
]);
export type ItineraryProcessTarget = z.infer<
  typeof itineraryProcessTargetSchema
>;
export const itineraryProcessTargetsSchema = z
  .array(itineraryProcessTargetSchema)
  .max(10)
  .refine(
    (steps) => new Set(steps).size === steps.length,
    'A process step cannot be repeated'
  );

export const itineraryStatusOption = z.object({
  id: z.string(),
  status: z.string().nullable(),
  color: z.string().nullable(),
  salesStage: z.number().int().nullable(),
  // Count of non-deleted itineraries currently assigned to this status.
  // Drives the disabled-Delete tooltip in the Edit Status dialog: a status
  // can only be hard-deleted once this reaches 0. Optional so consumers that
  // don't need it (and rows returned by create/update) stay valid.
  itineraryCount: z.number().int().optional(),
  // Count of soft-deleted (archived) itineraries still pointing at this
  // status. Invisible in every list, so they do NOT block the delete —
  // `deleteStatus` detaches them. Drives the Delete Status dialog's warning
  // that those records will lose their status.
  archivedItineraryCount: z.number().int().optional(),
  // Number of non-disabled automated message flows whose trigger list
  // includes this status. A status wired into a live flow must be
  // disconnected there first — deleting it would silently break the flow.
  activeFlowCount: z.number().int().optional(),
  // XT-521 — org-configured version-management lock. When true, an itinerary
  // in this status cannot have its active version changed and its non-active
  // versions are not editable. Optional so consumers built before the flag
  // existed (and older cached payloads) stay valid; absent reads as unlocked.
  activeVersionLocked: z.boolean().optional(),
  // Org-configured Traveller Preview access; null keeps the built-in rules.
  travellerPreview: travellerPreviewAccessSchema.nullable().optional(),
  // Org-configured approval-process step; null keeps name-based detection.
  processStep: itineraryProcessStepSchema.nullable().optional(),
  processSteps: itineraryProcessTargetsSchema.optional(),
});

export type ItineraryStatusOption = z.infer<typeof itineraryStatusOption>;

export const itinerarySearchOwner = z.object({
  id: z.string(),
  fullName: z.string().nullable(),
});

export type ItinerarySearchOwner = z.infer<typeof itinerarySearchOwner>;

// Customer lookup option for the /itineraries page Customers MultiSelect.
// `id` is the CRM contact UUID (matches the `customers` filter values).
export const itineraryCustomerOption = z.object({
  id: z.string(),
  name: z.string().nullable(),
});

export type ItineraryCustomerOption = z.infer<typeof itineraryCustomerOption>;

// One faceted filter-option count. `id` is the value that lands in the URL
// filter array (status/owner/customer UUID, label string, or duration-days
// as a string); `null` is reserved for the no-value bucket. Mirrors the
// products `filterCount` shape.
export const itineraryFilterCount = z.object({
  id: z.string().nullable(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});

export type ItineraryFilterCount = z.infer<typeof itineraryFilterCount>;

// Faceted counts powering the /itineraries toolbar filter dropdowns. Each
// dimension's count excludes its own active filter (so selecting an owner
// narrows the status/customer/label counts but the Owner dropdown still
// shows every owner), mirroring `products.filterCounts`.
export const itineraryFilterCounts = z.object({
  statuses: z.array(itineraryFilterCount),
  owners: z.array(itineraryFilterCount),
  customers: z.array(itineraryFilterCount),
  labels: z.array(itineraryFilterCount),
  durations: z.array(itineraryFilterCount),
});

export type ItineraryFilterCounts = z.infer<typeof itineraryFilterCounts>;

export const itinerarySearchCustomer = z.object({
  id: z.string(),
  name: z.string().nullable(),
  crmType: z.number().int().nullable(),
});

export type ItinerarySearchCustomer = z.infer<typeof itinerarySearchCustomer>;

export const itinerarySearchTravelerSummaryItem = z.object({
  title: z.string(),
  count: z.number().int().nonnegative(),
});

export type ItinerarySearchTravelerSummaryItem = z.infer<
  typeof itinerarySearchTravelerSummaryItem
>;

export const itinerarySearchActiveVersion = z.object({
  id: z.string(),
  startTime: z.string().nullable(),
  durationDays: z.number().int().nullable(),
  accountingReference: z.string().nullable(),
  viewedTimes: z.number().int().nullable(),
  price: z.string().nullable(),
  modifiedOn: z.string().nullable(),
  travelerSummary: z.array(itinerarySearchTravelerSummaryItem),
});

export type ItinerarySearchActiveVersion = z.infer<
  typeof itinerarySearchActiveVersion
>;

export const itinerarySearchItem = z.object({
  id: z.string(),
  title: z.string().nullable(),
  tags: z.array(z.string()),
  itineraryRef: z.string().nullable(),
  disabled: z.boolean(),
  // Customer-preview publish window. `previewPublishTime` is the UTC
  // timestamp the preview was published (null = unpublished);
  // `previewUnpublishTime` the optional scheduled expiry. The list table's
  // preview-indicator dot derives its colour from this pair.
  previewPublishTime: z.string().nullable(),
  previewUnpublishTime: z.string().nullable(),
  currency: z.string().nullable(),
  note: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  collaboratorAccountIds: z.array(z.string()),
  isEditable: z.boolean(),
  startTime: z.string().nullable(),
  finishTime: z.string().nullable(),
  durationDays: z.number().nullable(),
  status: itinerarySearchStatus.nullable(),
  owner: itinerarySearchOwner.nullable(),
  customer: itinerarySearchCustomer.nullable(),
  activeVersion: itinerarySearchActiveVersion.nullable(),
  // UTC ISO timestamp when the itinerary row was created
  // (`itineraries.itinerary.created_on`). Required for the list XLS
  // export "Created" column (XT-454); not shown on the table UI yet.
  createdOn: z.string(),
  // `itinerary.country` (enum) projected for the cms-public discovery
  // section + any future country chip on the consultant list. Null when
  // the row predates the column.
  country: z.string().nullable(),
  // Hero video `uploadthing_key` joined from `itineraries.regional_video`
  // via `itinerary.regional_video_id`. A VIDEO key — usable as a `<video>`
  // source, never as an `<img>`; consultant list ignores it.
  heroVideoKey: z.string().nullable(),
  // XT-723 — unanswered Customer Preview comments. Per discussion thread
  // (general / day), the messages from the customer or agent side that are
  // newer than that thread's last consultant reply, summed across the
  // itinerary's threads. `count > 0` is what flags the row in the consultant
  // list; a consultant reply on a thread clears that thread's share.
  // Absent — not `count: 0` — when the caller opted out via
  // `withPendingComments: false` (the XLS export), so "no data" never reads
  // as "nothing pending".
  pendingComments: z
    .object({
      count: z.number().int().nonnegative(),
      // UTC ISO timestamp of the newest unanswered message, null when none.
      latestAt: z.string().nullable(),
      // `author_label` of that newest unanswered message.
      latestAuthor: z.string().nullable(),
    })
    .optional(),
  // Hero image `uploadthing` key — the CMS row's `hero_image`, synced from the
  // active version's first IMAGE attachment (the Photos settings tab). This is
  // what the cms-public itinerary card renders as its thumbnail. Null on the
  // main-DB search: the column lives only in the separate CMS database.
  heroImageKey: z.string().nullable(),
  // XT-692 — the CMS row's `cms_title` (storefront title per language). Only
  // the storefront read sets it; absent on the main-DB search.
  cmsTitle: z
    .array(z.object({ languageCode: z.string(), text: z.string() }))
    .nullable()
    .optional(),
});

export type ItinerarySearchItem = z.infer<typeof itinerarySearchItem>;

export const itinerarySearchResult = z.object({
  data: z.array(itinerarySearchItem),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});

export type ItinerarySearchResult = z.infer<typeof itinerarySearchResult>;

// Result of `itineraries.exportExcel` — base64-encoded XLSX. Mirrors
// `exportPaymentLogsResultSchema` so the FE can reuse the same
// base64-to-Blob download helper for both surfaces.
export const exportItinerariesResultSchema = z.object({
  base64: z.string(),
  filename: z.string(),
  mime: z.literal(
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ),
});

export type ExportItinerariesResult = z.infer<
  typeof exportItinerariesResultSchema
>;

// --- Cancelled-itineraries report (admin-only modal on /itineraries) ---------
//
// A distinct surface from the itinerary list: its own input (a subset of the
// list's filters, plus the cancelled-date range), its own row shape (the eight
// columns the report specifies, nothing else), and its own 8-column XLSX. It
// deliberately does NOT reuse `itinerarySearchItem` / `buildItinerariesXlsx` —
// the consultant list's 14-column export must stay exactly as it is.
//
// Cancelled-ness is decided server-side by `sales_stage = 20`, so no caller can
// widen the report to non-cancelled itineraries.

export const cancelledItinerariesReportInput = z
  .object({
    reference: z.string().optional(),
    title: z.string().optional(),
    customerName: z.string().optional(),
    owners: oneOrMany(z.uuid()).optional(),
    customers: oneOrMany(z.uuid()).optional(),
    // Inclusive range on `itinerary.created_on`.
    createdStartDate: dateOnly.optional(),
    createdEndDate: dateOnly.optional(),
    // Inclusive range on `itinerary.cancelled_on` — the date the itinerary
    // moved to a Cancelled-stage status.
    cancelledStartDate: dateOnly.optional(),
    cancelledEndDate: dateOnly.optional(),
    // Inclusive bounds on the active version's price (the report's "Value").
    minValue: z.number().nonnegative().optional(),
    maxValue: z.number().nonnegative().optional(),
    sortBy: z
      .enum([
        'reference',
        'title',
        'createdOn',
        'owner',
        'customer',
        'value',
        'cancelledOn',
      ])
      .optional(),
    sortOrder: itinerarySearchSortOrder.optional(),
    offset: z.number().int().nonnegative().default(0),
    limit: z.number().int().positive().max(100).default(20),
  })
  .strict()
  .superRefine((input, ctx) => {
    // Both date ranges are all-or-nothing, matching the list's convention.
    if (
      (input.createdStartDate && !input.createdEndDate) ||
      (!input.createdStartDate && input.createdEndDate)
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'createdStartDate and createdEndDate must be provided together',
        path: input.createdStartDate
          ? ['createdEndDate']
          : ['createdStartDate'],
      });
    }
    if (
      (input.cancelledStartDate && !input.cancelledEndDate) ||
      (!input.cancelledStartDate && input.cancelledEndDate)
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'cancelledStartDate and cancelledEndDate must be provided together',
        path: input.cancelledStartDate
          ? ['cancelledEndDate']
          : ['cancelledStartDate'],
      });
    }
  });

export type CancelledItinerariesReportInput = z.infer<
  typeof cancelledItinerariesReportInput
>;

// One report row — exactly the eight columns the report specifies. `value` is
// the active version's stored `price` (a numeric string, base EUR) with its
// currency alongside for display; `cancelledOn` / `cancellationReason` are
// nullable because a cancellation predating XT-264 has no stamped date and the
// bulk-cancel path records no reason.
export const cancelledItineraryReportRow = z.object({
  id: z.string(),
  itineraryRef: z.string().nullable(),
  title: z.string().nullable(),
  createdOn: z.string(),
  ownerName: z.string().nullable(),
  customerName: z.string().nullable(),
  value: z.string().nullable(),
  currency: z.string().nullable(),
  cancelledOn: z.string().nullable(),
  cancellationReason: z.string().nullable(),
});

export type CancelledItineraryReportRow = z.infer<
  typeof cancelledItineraryReportRow
>;

export const cancelledItinerariesReportResult = z.object({
  data: z.array(cancelledItineraryReportRow),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  // Cancelled rows in the whole filtered match with no stamped `cancelled_on`
  // (see `itinerarySearchResult.missingCancelledOn`). Drives the modal's
  // "N have no recorded cancellation date" note.
  missingCancelledOn: z.number(),
});

export type CancelledItinerariesReportResult = z.infer<
  typeof cancelledItinerariesReportResult
>;
